import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router-dom";

// import { IconEdit01 } from "@pentabd/icons";
import { Table } from "@pentabd/ui";

import { ErrorModal, SearchBox, SuccessModal } from "@components";
import { AddExtraResponsibilityModal } from "./AddExtraResponsibilityModal";
import { TableColumn } from "./TableColumn";

// import { useEmployees } from "@hooks/HRM/employees/useEmployees";

import { TablePagination } from "@helpers/tablePagination";

import { STATUS } from "@constants/status.hrm";

import { createExtraResponsibility } from "@api/employee";

import { OutletContextType } from "@type/layout";
import {
  ExtraResponsibilityFormDataType,
  extraResponsibilityValidation,
} from "@validations/HRM/employee/extraResponsibilityValidation";
import { transformToReadableString } from "@utils/transformText";
import { useEmployeesForTable } from "@hooks/HRM/employees/useEmployeesForTable";

const { SUCCESS, FAILED } = STATUS;

export const EmployeeTable = () => {
  const navigate = useNavigate();
  const { control } = useForm();
  const { t } = useTranslation();

  const { onRefetchHeaderState } = useOutletContext<OutletContextType>();

  const [addExtraResponsibilityModalOpen, setAddExtraResponsibilityModalOpen] =
    useState<boolean>(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);

  const [
    addExtraResponsibilityEmployeeId,
    setAddExtraResponsibilityEmployeeId,
  ] = useState<number | null>(null);

  // const { employees, pagination, employeeRequest, handleQuery } =
  //   useEmployees();
  const { employees, handleQuery, loading, pagination } =
    useEmployeesForTable();

  const { total, size, page } = pagination || {};
  const methods = useForm<ExtraResponsibilityFormDataType>({
    resolver: yupResolver(extraResponsibilityValidation),
  });

  const { handleSubmit, reset } = methods;

  const onSubmit: SubmitHandler<ExtraResponsibilityFormDataType> = async (
    data: any,
  ) => {
    setSubmitLoading(true);
    createExtraResponsibility(
      {
        ...data,
        currentEmployeeId: addExtraResponsibilityEmployeeId,
      },
      (res) => {
        if (res.status === SUCCESS) {
          setIsSuccessModalOpen(true);
          setSubmitLoading(false);
          setSuccessMessage(`${transformToReadableString(data.type)} created`);
        } else if (res.status === FAILED) {
          setSubmitLoading(false);
          setIsErrorModalOpen(true);
        }
      },
    );
  };

  const onAddExtraResponsibilityOpen = (id: number | null) => {
    setAddExtraResponsibilityModalOpen(true);
    setAddExtraResponsibilityEmployeeId(id);
  };

  const onAddExtraResponsibilityClose = (id: number | null) => {
    reset();
    setAddExtraResponsibilityModalOpen(false);
    setAddExtraResponsibilityEmployeeId(id);
  };

  const successAction = () => {
    reset();
    setIsSuccessModalOpen(false);
    onAddExtraResponsibilityClose(null);
    onRefetchHeaderState();
    setSuccessMessage("");
  };

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
  };

  useEffect(() => {
    const reset = () => {
      setIsErrorModalOpen(false);
      setIsSuccessModalOpen(false);
    };
    return reset();
  }, []);

  return (
    <>
      <Table
        loading={loading}
        header={{
          title: t("EMPLOYEES.TABLE.HEADER"),
          description: t("EMPLOYEES.DESCRIPTION"),
          badge: {
            label: `${total || 0}`,
            size: "sm",
            type: "primary",
          },

          buttons: [
            <SearchBox
              key="search"
              large
              control={control}
              handleQuery={handleQuery}
            />,
          ],
        }}
        columns={TableColumn(t, onAddExtraResponsibilityOpen, navigate) as any}
        // edit={{
        //   customIcon: getAccess(roles, "EMPLOYEE_UPDATE_PAGE") && (
        //     <IconEdit01 size={24} fill="subtitle1" />
        //   ),
        //   onClick: (id) =>
        //     getAccess(roles, "EMPLOYEE_UPDATE_PAGE") &&
        //     navigate(`${EMPLOYEES}/${id}`),
        // }}
        rows={employees || []}
        pagination={TablePagination({ total, size, page }, handleQuery)}
      />
      {/* modals  */}
      {addExtraResponsibilityModalOpen && (
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <AddExtraResponsibilityModal
              addExtraResponsibilityModalOpen={addExtraResponsibilityModalOpen}
              onAddExtraResponsibilityClose={onAddExtraResponsibilityClose}
              loading={submitLoading}
            />
          </form>
        </FormProvider>
      )}
      <SuccessModal
        isModalOpen={isSuccessModalOpen}
        action={successAction}
        subtitle={successMessage}
      />
      <ErrorModal isModalOpen={isErrorModalOpen} closeModal={closeErrorModal} />
    </>
  );
};
